import {
  Stack,
  Typography,
  Select,
  MenuItem,
  Chip,
  SelectProps,
  ChipProps,
} from '@mui/material';
import { useCurrentReportDate } from '@features/campaigns/subdomains/visitation/hooks/useReports';
import { useAppDispatch } from '@utils/hooks';
import { reportSelected } from '@components/Dashboard/dashboardSlice';
import { dateFormatters } from '@utils/formattersV2';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { ChannelFilter } from './DateFilter/ChannelFilter';
import React, { useMemo } from 'react';

const buildStatusChipProps = (
  campaignEndDate: string
): Pick<ChipProps, 'sx' | 'label'> =>
  new Date() > new Date(campaignEndDate)
    ? {
        sx: {
          backgroundColor: unifiedPlatformColorPalette.lightActionActive,
        },
        label: 'Campaign Complete',
      }
    : {
        sx: { backgroundColor: unifiedPlatformColorPalette.lightSuccess },
        label: 'Campaign Active',
      };

type DateFilterProps = {
  campaignId: number;
  measurementStartDate: string;
  campaignEndDate: string;
  endReportDates: readonly string[];
  channels: readonly string[];
  children?: React.ReactNode;
};

export const DateFilter = (props: DateFilterProps) => {
  const { measurementStartDate } = props;
  const dispatch = useAppDispatch();
  const selectedReportDate = useCurrentReportDate();
  const statusChipProps = buildStatusChipProps(props.campaignEndDate);
  const endReportDates = useMemo(
    () =>
      props.endReportDates.toSorted((a, b) => Date.parse(b) - Date.parse(a)),
    [props.endReportDates]
  );

  const endReportDateOptions = endReportDates.map(reportDate => (
    <MenuItem key={reportDate} value={reportDate}>
      {dateFormatters.shortLocaleString(new Date(reportDate))}
    </MenuItem>
  ));

  const handleEndDateChange: SelectProps['onChange'] = event => {
    dispatch(reportSelected(event.target.value as string));
  };

  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <ChannelFilter channels={props.channels} campaignId={props.campaignId} />
      <Typography>Show results from</Typography>
      {/* Change to an input when variable start date is supported */}
      <Typography>
        {dateFormatters.shortLocaleString(new Date(measurementStartDate))}
      </Typography>
      <Typography>to</Typography>
      <Select
        sx={{ width: '220px', backgroundColor: 'white' }}
        size="small"
        value={selectedReportDate ?? ''}
        onChange={handleEndDateChange}
        disabled={endReportDates.length === 0}
      >
        {endReportDateOptions}
      </Select>
      <Chip
        label={statusChipProps.label}
        size="small"
        sx={{
          color: 'white',
          px: '0.25rem',
          fontWeight: 500,
          ...statusChipProps.sx,
        }}
      />
      {props.children}
    </Stack>
  );
};
